"use client";

import React, { useState } from "react";
import * as XLSX from "xlsx";
import type { jsPDF } from "jspdf";
import Image from "next/image";
import { useLoginModal } from "@/utils/useLoginModal";
import { NormalizedRollingTableRow, TableRollingReturns } from "@/type/rollingPageTypes";
import { buildRollingInsightsTableData, buildRollingNavTableData } from "@/utils/rollingReturnsCalculation";
import { ROOT_URL } from "@/utils/gloableVariables";

type Props = {
  tableData: TableRollingReturns[];
  tabularView: boolean;
};

type PdfOptions = {
  margin: number;
  filename: string;
  image: {
    type: "jpeg" | "png" | "webp";
    quality: number;
  };
  html2canvas: {
    scale: number;
    useCORS: boolean;
  };
  jsPDF: {
    unit: "mm";
    format: "a4";
    orientation: "landscape" | "portrait";
  };
  pagebreak?: {
    mode: Array<"avoid-all" | "css" | "legacy">;
  };
};

export default function DownloadPDFRollingTable({ tableData, tabularView }: Props) {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingXLS, setIsGeneratingXLS] = useState(false);

  const { isLoggedIn, open, setModalKey } = useLoginModal();

  /* ------------------------------------------------------ */
  /* ---------------- BUILD TABLE HTML -------------------- */
  /* ------------------------------------------------------ */

  const buildPdfTableHtml = (): string => {
    const tableAllData = tabularView
      ? buildRollingInsightsTableData(tableData, true)
      : buildRollingNavTableData(tableData);

    const { title, columns } = tableAllData;

    const totalCols = columns.length + 1;
    const colWidth = 100 / totalCols;

    let html = `
    <style>
      .rolling-table-wrapper table {
        border-collapse: collapse;
        width: 100%;
        table-layout: fixed;
        font-size: 11px;
      }
      .rolling-table-wrapper th, td {
        border: 1px solid #CBD5E1;
        padding: 6px;
        word-wrap: break-word;
      }
      .rolling-table-wrapper th {
        background: #F1F5F9;
        font-weight: 700;
        text-align: center;
      }
      .rolling-table-wrapper td.metric {
        font-weight: 600;
        background: #FAFBFE;
      }
      .rolling-table-wrapper td.metric-rolling:first-of-type {
        font-weight: 600;
        background: #FAFBFE;
      }
      .rolling-table-wrapper {
        width: 100%;
        padding: 18px;
        font-family: Arial, sans-serif;
      }
      col { width: ${colWidth}% }
    </style>

    <div class="rolling-table-wrapper">
      <div style="text-align:center;font-size:18px;margin-bottom:12px;color:#0e6799;">
        ${title}
      </div>
      <table>
        <colgroup>
  `;

    for (let i = 0; i < totalCols; i++) {
      html += `<col />`;
    }

    html += `
        </colgroup>
        <thead>
          <tr>
            ${tabularView ? "" : "<th>Date</th>"}
  `;

    columns.forEach((col) => {
      html += `<th>${col}</th>`;
    });

    html += `
          </tr>
        </thead>
        <tbody>
  `;

    /* ---------------- TYPE SAFE ROW HANDLING ---------------- */

    if (tabularView) {
      // INSIGHTS TABLE
      tableAllData.rows.forEach((row) => {
        const insightRow = row as NormalizedRollingTableRow;

        html += `<tr>`;
        columns.forEach((col) => {
          if (col === "Consistency") {
            const consistency = insightRow.Consistency;

            const display = consistency?.consistencyPercentage ?? "-";

            html += `<td class="metric-rolling" style="text-align:center">${display}</td>`;
          } else {
            const value = insightRow[col as Exclude<keyof NormalizedRollingTableRow, "Consistency">];

            html += `<td class="metric-rolling" style="text-align:center">${value ?? "-"}</td>`;
          }
        });
        html += `</tr>`;
      });
    } else {
      // NAV TABLE
      const navRows = tableAllData.rows as {
        label: string;
        values: string[];
      }[];

      navRows.forEach((row) => {
        html += `<tr>`;
        html += `<td class="metric">${row.label}</td>`;

        row.values.forEach((val) => {
          html += `<td style="text-align:center">${Number(val).toFixed(2)}%</td>`;
        });

        html += `</tr>`;
      });
    }

    html += `
        </tbody>
      </table>
    </div>
  `;

    return html;
  };

  /* ---------------- PDF DOWNLOAD ------------------------ */
  const handlePDF = async (): Promise<void> => {
    if (!isLoggedIn) {
      open();
      setModalKey("rolling-returns-pdf-download");
      return;
    }

    setIsGeneratingPDF(true);

    const html = buildPdfTableHtml();
    const container = document.createElement("div");
    container.innerHTML = html;

    const options: PdfOptions = {
      margin: 10,
      filename: "rolling-returns-report.pdf",
      pagebreak: { mode: ["avoid-all", "css"] },
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
    };

    const html2pdf = (await import("html2pdf.js")).default;

    await html2pdf()
      .set(options)
      .from(container)
      .toPdf()
      .get("pdf")
      .then((pdf: jsPDF) => {
        pdf.save("rolling-returns-report.pdf");
      })
      .finally(() => {
        setIsGeneratingPDF(false);
      });
  };

  /* ---------------- EXCEL DOWNLOAD ---------------------- */
  const handleExcel = (): void => {
    if (!isLoggedIn) {
      open();
      setModalKey("rolling-returns-excel-download");
      return;
    }

    setIsGeneratingXLS(true);

    try {
      const tableAllData = tabularView
        ? buildRollingInsightsTableData(tableData, true)
        : buildRollingNavTableData(tableData);

      const { columns } = tableAllData;

      const excelRows: Record<string, string | number>[] = [];

      if (tabularView) {
        tableAllData.rows.forEach((row) => {
          const insightRow = row as NormalizedRollingTableRow;

          const formattedRow: Record<string, string | number> = {};

          columns.forEach((col) => {
            if (col === "Consistency") {
              formattedRow[col] = insightRow.Consistency?.consistencyPercentage ?? "-";
            } else {
              const value = insightRow[col as Exclude<keyof NormalizedRollingTableRow, "Consistency">];

              formattedRow[col] = value ?? "-";
            }
          });

          excelRows.push(formattedRow);
        });
      } else {
        const navRows = tableAllData.rows as {
          label: string;
          values: string[];
        }[];

        navRows.forEach((row) => {
          const r: Record<string, string> = {
            Date: row.label,
          };

          columns.forEach((col, idx) => {
            r[col] = Number(row.values[idx]).toFixed(2);
          });

          excelRows.push(r);
        });
      }

      const header = tabularView ? columns : ["Date", ...columns];

      const sheet = XLSX.utils.aoa_to_sheet([header]);

      XLSX.utils.sheet_add_json(sheet, excelRows, {
        origin: "A2",
        skipHeader: true,
      });

      /* ------------------ COLUMN WIDTH ------------------ */
      sheet["!cols"] = header.map((col, index) => {
        if (tabularView) {
          if (index === 0) {
            return { wpx: 320 };
          }

          // auto width based on header length (minimum 120px)
          const approx = Math.max(col.length * 9, 120);
          return { wpx: approx + 16 }; // small horizontal padding feel
        }

        // NAV TABLE MODE
        if (index === 0) {
          return { wpx: 280 }; // wide enough for date/labels without wrap
        }

        return { wpx: 320 };
      });

      /* ------------------ HEADER STYLE ------------------ */
      const range = XLSX.utils.decode_range(sheet["!ref"] ?? "A1");

      for (let r = 0; r <= range.e.r; r++) {
        for (let c = 0; c <= range.e.c; c++) {
          const cellAddress = XLSX.utils.encode_cell({ r, c });
          const cell = sheet[cellAddress];
          if (!cell) continue;

          const isHeader = r === 0;
          const isFirstColumn = c === 0;

          cell.s = {
            font: {
              bold: isHeader,
              color: { rgb: "000000" },
            },
            fill: isHeader
              ? {
                  patternType: "solid",
                  fgColor: { rgb: "DCEEFF" }, // light blue
                }
              : undefined,
            alignment: {
              horizontal: isFirstColumn ? "left" : "center",
              vertical: "top",
              wrapText: tabularView
                ? true // wrap allowed in tabular mode
                : !isFirstColumn, // wrap others only in nav mode
            },
          };
        }
      }

      /* ------------------ WRAP TEXT FOR ALL CELLS ------------------ */
      for (let rowIndex = 0; rowIndex <= range.e.r; rowIndex++) {
        for (let colIndex = 0; colIndex <= range.e.c; colIndex++) {
          const cellAddress = XLSX.utils.encode_cell({
            r: rowIndex,
            c: colIndex,
          });

          const cell = sheet[cellAddress];
          if (!cell) continue;

          cell.s = {
            ...cell.s,
            alignment: {
              wrapText: true,
              vertical: "top",
            },
          };
        }
      }

      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "Rolling Returns");

      XLSX.writeFile(book, "rolling-returns-report.xlsx");
    } finally {
      setIsGeneratingXLS(false);
    }
  };

  return (
    <div className="flex w-fit mx-auto items-center gap-3 mb-6">
      <span className="font-medium text-[#004B6B]">Download in</span>
      <button
        onClick={handlePDF}
        disabled={isGeneratingPDF}
        aria-label="Download PDF Report File"
        className="w-11 h-9 flex items-center justify-center rounded-lg border border-[#56565640] relative"
      >
        {isGeneratingPDF ? (
          <div className="transform w-5 h-5 border-[3px] border-solid border-transparent border-t-[#0c3e9f] rounded-full animate-spin" />
        ) : (
          <Image
            src={`${ROOT_URL}images/swp-calculator/pdf.svg`}
            alt="PDF Icon"
            width={20}
            height={24}
            className="w-5"
          />
        )}
      </button>

      <button
        onClick={handleExcel}
        disabled={isGeneratingXLS}
        aria-label="Download XLSX Report File"
        className="w-11 h-9 flex items-center justify-center rounded-lg border border-[#56565640] relative"
      >
        {isGeneratingXLS ? (
          <div className="transform w-5 h-5 border-[3px] border-solid border-transparent border-t-[#0c3e9f] rounded-full animate-spin" />
        ) : (
          <Image
            src={`${ROOT_URL}images/swp-calculator/excel.svg`}
            alt="Excel Icon"
            width={20}
            height={24}
            className="w-5"
          />
        )}
      </button>
    </div>
  );
}
